import { useEffect, useRef, useState } from 'react';
import { Building2, Plus } from 'lucide-react';
import { crm, type OrganizationRow } from '@/lib/crm-client';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';

interface Props {
  /** Der Name, wie er im Feld steht. */
  value: string;
  onChange: (name: string) => void;
  /**
   * Eine bestehende Firma wurde gewaehlt — oder `null`, sobald jemand weiter
   * tippt und der Name damit wieder frei ist.
   */
  onSelect?: (firma: OrganizationRow | null) => void;
  /** Schraenkt die Vorschlaege auf eine Zielgruppe ein. */
  audienceId?: string;
  placeholder?: string;
  id?: string;
  className?: string;
}

/**
 * Firmenname mit Vorschlaegen beim Tippen.
 *
 * Vorher stand hier ein `<datalist>`. Das zeigt je nach Browser gar nichts
 * an, und wo doch, filtert es die Server-Treffer noch einmal nach eigenen
 * Regeln — beim Tippen erschien deshalb oft keine Auswahl. Hier kommt die
 * Liste immer, der naechste Treffer ist vorgewaehlt und mit Enter
 * uebernommen. Wer eine neue Firma meint, schreibt weiter: der Name bleibt
 * Freitext und wird beim Speichern angelegt.
 */
export function FirmenFeld({
  value,
  onChange,
  onSelect,
  audienceId,
  placeholder = 'Firma suchen oder neu eingeben …',
  id,
  className,
}: Props) {
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(0);
  const huelle = useRef<HTMLDivElement>(null);

  const { data, loading } = useDebouncedSearch(
    () =>
      crm.organizations.list({
        limit: 8,
        ...(audienceId ? { audienceId } : {}),
        ...(value.trim() ? { search: value.trim() } : {}),
      }) as Promise<OrganizationRow[]>,
    [value, audienceId],
    { enabled: offen, delay: 150 },
  );
  const treffer = data ?? [];
  const exakt = treffer.some((o) => o.name.trim().toLowerCase() === value.trim().toLowerCase());

  useEffect(() => setMarkiert(0), [data]);

  useEffect(() => {
    if (!offen) return;
    const aufKlick = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', aufKlick);
    return () => document.removeEventListener('mousedown', aufKlick);
  }, [offen]);

  const waehlen = (o: OrganizationRow) => {
    onChange(o.name);
    onSelect?.(o);
    setOffen(false);
  };

  const aufTaste = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!offen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOffen(true);
      return;
    }
    if (!offen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarkiert((i) => Math.min(i + 1, treffer.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarkiert((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const t = treffer[markiert];
      if (t) {
        // Enter uebernimmt den Vorschlag, statt das Formular abzuschicken.
        e.preventDefault();
        waehlen(t);
      }
    } else if (e.key === 'Escape') {
      // Nur die Liste zu, nicht der Dialog drumherum.
      e.preventDefault();
      e.stopPropagation();
      setOffen(false);
    } else if (e.key === 'Tab') {
      setOffen(false);
    }
  };

  return (
    <div ref={huelle} className={cn('relative', className)}>
      <Input
        id={id}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-expanded={offen}
        aria-autocomplete="list"
        placeholder={placeholder}
        onFocus={() => setOffen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          onSelect?.(null);
          setOffen(true);
        }}
        onKeyDown={aufTaste}
      />

      {offen && (loading || treffer.length > 0 || value.trim()) && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-lg"
        >
          {loading && treffer.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">Suche …</li>
          )}
          {treffer.map((o, i) => (
            <li key={o.id} role="option" aria-selected={i === markiert}>
              <button
                type="button"
                onMouseEnter={() => setMarkiert(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => waehlen(o)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                  i === markiert ? 'bg-accent text-accent-foreground' : '',
                )}
              >
                <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate">{o.name}</span>
                  {(o.city || o.isCustomer) && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {[o.city, o.isCustomer ? 'Kunde' : null].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
          {!loading && value.trim() && !exakt && (
            <li className="flex items-center gap-2 border-t px-3 py-1.5 text-xs text-muted-foreground">
              <Plus className="h-3.5 w-3.5 shrink-0" />„{value.trim()}“ wird als neue Firma angelegt
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
